'use client';

import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'next-i18next';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Modal, Spinner, bjToast } from '@components/common';
import { ChatMessageComponent } from '@components/templates/homepage/chat/ChatMessageComponent';
import {
  ResumeInterviewChatProvider,
  useResumeInterviewChatState,
} from '@components/contexts/ResumeInterviewChatContext';
import type { ResumeInterviewKind } from '@services/voice/voiceSessionApi';

export interface ResumeInterviewChatModalProps {
  getQuestions: () => Promise<string[]>;
  locale: Locale;
  /** Interview variant: 'profile' (default) vs 'answer' (story-card shout-back). */
  kind?: ResumeInterviewKind;
}

/**
 * Inner body — must live inside `ResumeInterviewChatProvider` to consume the chat state.
 * On mount it resolves the questions and starts the backend session. Once ready it renders the
 * streamed messages via the shared `ChatMessageComponent` plus a minimal text input.
 */
const ResumeInterviewChatBody = ({
  getQuestions,
  onClose,
}: ResumeInterviewChatModalProps & { onClose: () => void }) => {
  const { t } = useTranslation('common');
  const {
    stream, isReady, isStarting, startSession, submitChat,
  } = useResumeInterviewChatState();

  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const startedRef = useRef(false);
  const listEndRef = useRef<HTMLDivElement | null>(null);

  // Resolve questions once and start the backend session. Guard against double-invocation
  // (React strict mode) and against an empty question set.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      try {
        const resolved = await getQuestions();
        if (!resolved.length) {
          bjToast.error(t('audio-interview.job-landing.error'));
          onClose();
          return;
        }
        await startSession(resolved);
      } catch {
        bjToast.error(t('audio-interview.error.connecting'));
        onClose();
      }
    })();
  }, [getQuestions, startSession, onClose, t]);

  // Keep the newest message in view.
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [stream?.messages, stream?.status]);

  const isStreaming = stream?.status === 'inflight';

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isSending || isStreaming) return;
    setInput('');
    setIsSending(true);
    try {
      await submitChat(text);
    } catch {
      bjToast.error(t('audio-interview.job-landing.error'));
      setInput(text);
    } finally {
      setIsSending(false);
    }
  }, [input, isSending, isStreaming, submitChat]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  }, [handleSend]);

  if (isStarting || !isReady) {
    return (
      <div className="flex min-h-64 flex-col p-6">
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('global.btn.close')}
            className="flex size-9 items-center justify-center rounded-full text-ink-medium transition-colors hover:bg-surface-100"
          >
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      </div>
    );
  }

  const messages = stream?.messages ?? [];
  const sendDisabled = isSending || isStreaming || !input.trim();

  return (
    <div className="flex h-[70vh] max-h-[70vh] flex-col p-6">
      {/* Header with dismiss */}
      <div className="mb-2 flex items-center justify-end">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('global.btn.close')}
          className="flex size-9 items-center justify-center rounded-full text-ink-medium transition-colors hover:bg-surface-100"
        >
          <XMarkIcon className="size-5" />
        </button>
      </div>

      {/* Scrollable message list */}
      <div className="flex-1 space-y-4 overflow-y-auto px-1 py-2">
        {messages.map((message) => (
          <ChatMessageComponent
            key={`resume-interview-message-${message.id}`}
            message={{ ...message }}
          />
        ))}
        <div ref={listEndRef} />
      </div>

      {/* Minimal composer */}
      <div className="mt-2 flex items-end gap-2 border-t border-ink-border pt-3">
        <textarea
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          className="flex-1 resize-none rounded-2xl border border-ink-border px-4 py-2 text-sm focus:border-secondary focus:outline-none"
          placeholder={t('global.placeholder.answer')}
          disabled={isSending}
        />
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={sendDisabled}
          aria-label={t('global.action.send')}
          className={clsx(
            'flex size-10 shrink-0 items-center justify-center rounded-full text-white transition-colors',
            sendDisabled ? 'bg-ink-light' : 'bg-secondary hover:bg-secondary/90',
          )}
        >
          <PaperAirplaneIcon className="size-5" />
        </button>
      </div>
    </div>
  );
};

/**
 * Text-channel resume interview modal. Wraps the chat body in `ResumeInterviewChatProvider` so the
 * chat runs over the backend-built session (thread_id + prompt + extraction_schema).
 */
export const ResumeInterviewChatModal = NiceModal.create<ResumeInterviewChatModalProps>(
  ({ getQuestions, locale, kind = 'profile' }) => {
    const modal = useModal();

    const closeModal = useCallback(() => {
      void modal.hide();
      modal.remove();
    }, [modal]);

    return (
      <Modal
        isOpen={modal.visible}
        onClose={closeModal}
        size="lg"
        className="!rounded-3xl"
        body={(
          <ResumeInterviewChatProvider locale={locale} kind={kind}>
            <ResumeInterviewChatBody getQuestions={getQuestions} locale={locale} onClose={closeModal} />
          </ResumeInterviewChatProvider>
        )}
      />
    );
  },
);
